import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title: string;
    description: string;
    name?: string;
    type?: string;
    image?: string;
    url?: string;
}

export function SEO({
    title,
    description,
    name = 'MCP Cloud',
    type = 'website',
    image = 'https://mcp.cloud/og-image.png',
    url = 'https://mcp.cloud',
}: SEOProps) {
    const defaultTitle = 'MCP Cloud — Connect AI to everything. Let it do anything.';
    const fullTitle = title === defaultTitle ? title : `${title} | MCP Cloud`;

    // JSON-LD structured data for this specific page
    const webPageSchema = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: fullTitle,
        description,
        url,
        isPartOf: { '@id': 'https://mcp.cloud/#website' },
        about: { '@id': 'https://mcp.cloud/#organization' },
    });

    return (
        <Helmet>
            {/* Standard metadata tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />

            {/* Open Graph tags for social media links */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1024" />
            <meta property="og:image:height" content="1024" />
            {url && <meta property="og:url" content={url} />}
            {url && <link rel="canonical" href={url} />}
            <meta property="og:site_name" content={name} />

            {/* Twitter Card tags */}
            <meta name="twitter:creator" content={name} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* JSON-LD WebPage structured data */}
            <script type="application/ld+json">{webPageSchema}</script>
        </Helmet>
    );
}
